import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  FaSearch,
  FaEye,
  FaTimes,
  FaSyncAlt,
  FaFileCsv,
  FaFlag,
  FaCalendarDay,
  FaCalendarWeek,
  FaUserSlash,
  FaUserCircle,
  FaPhoneAlt,
  FaCopy,
  FaBan,
  FaExternalLinkAlt,
  FaChevronLeft,
  FaChevronRight,
  FaExclamationTriangle,
  FaInbox,
  FaTrash,
} from "react-icons/fa";
import { Toast } from "primereact/toast";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes rpFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes rpFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes rpSlide { from { transform: translateX(100%); } to { transform: none; } }
.rp-in { animation: rpFadeUp .45s cubic-bezier(.2,.7,.2,1) both; }
.rp-fade { animation: rpFade .2s ease both; }
.rp-slide { animation: rpSlide .3s cubic-bezier(.2,.8,.2,1) both; }
.rp-press { transition: transform .15s ease, box-shadow .2s ease, background-color .2s ease, border-color .2s ease; }
.rp-press:hover { transform: translateY(-1px); }
.rp-press:active { transform: scale(.97); }
.rp-row { transition: background-color .15s ease; cursor: pointer; }
@media (prefers-reduced-motion: reduce) {
  .rp-in, .rp-fade, .rp-slide { animation: none; }
  .rp-press:hover, .rp-press:active { transform: none; }
}
`;

const CARD =
  "bg-white dark:bg-secondary-dark-bg rounded-xl border border-slate-200 dark:border-slate-700";

/* ================= helpers ================= */

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

const parseDate = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtDate = (d) =>
  d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
const fmtTime = (d) =>
  d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";

const DAY = 24 * 60 * 60 * 1000;

const Avatar = ({ src, name, size = 40 }) => {
  const [err, setErr] = useState(false);
  const initials =
    String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  if (src && !err) {
    return (
      <img
        src={src}
        alt=""
        onError={() => setErr(true)}
        className="rounded-full object-cover shrink-0 bg-slate-100"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      className="rounded-full shrink-0 flex items-center justify-center bg-slate-200 text-slate-600 font-semibold dark:bg-slate-600 dark:text-slate-200"
      style={{ width: size, height: size, fontSize: size * 0.38 }}
    >
      {initials}
    </span>
  );
};

/* ================= page ================= */

const ReportedUser = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkWorking, setBulkWorking] = useState(false);

  const [query, setQuery] = useState("");
  const [range, setRange] = useState("all"); // all | today | 7d | 30d
  const [sortBy, setSortBy] = useState("newest"); // newest | oldest | most
  const [focusUser, setFocusUser] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const toast = useRef(null);
  const { currentMode } = useStateContext();
  const { baseUrl } = useBaseUrl();

  const api = useMemo(
    () =>
      axios.create({
        baseURL: `${baseUrl}/`,
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      }),
    [baseUrl]
  );

  /* ----- load reports (same API as before) ----- */

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("action", "reported-users");
      formData.append("reported", "0");

      const res = await api.post("/users.php", formData);

      if (Array.isArray(res.data)) {
        setReports(res.data);
      } else if (res.data && Array.isArray(res.data.reports)) {
        setReports(res.data.reports);
      } else if (res.data && Array.isArray(res.data.data)) {
        setReports(res.data.data);
      } else {
        console.error("API response is not an array:", res.data);
        setReports([]);
      }
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch reported users",
      });
    }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  /* ----- block user (same API as before) ----- */

  const acceptRequest = async (report_id) => {
    setBlocking(true);
    try {
      const formData = new FormData();
      formData.append("action", "report-action");
      formData.append("report_id", report_id);

      await api.post("/users.php", formData);

      toast.current?.show({
        severity: "success",
        summary: "User Blocked",
        detail: "Reported user blocked successfully",
      });

      closeDrawer();
      fetchReports();
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to block user",
      });
    }
    setBlocking(false);
  };

  /* ----- remove report (delete-report API) ----- */

  const deleteReport = async (report_id) => {
    const formData = new FormData();
    formData.append("action", "delete-report");
    formData.append("report_id", report_id);
    const res = await api.post("/users.php", formData);
    if (res.data && (res.data.status === false || res.data.error === 1)) {
      throw new Error(res.data.message || res.data.error || "Delete failed");
    }
    return res.data;
  };

  const handleRemove = async (report_id) => {
    setBlocking(true);
    try {
      await deleteReport(report_id);
      toast.current?.show({
        severity: "success",
        summary: "Report Removed",
        detail: `Report #${report_id} removed`,
      });
      setReports((rs) => rs.filter((r) => r.report_id !== report_id));
      setSelectedIds((ids) => ids.filter((id) => id !== report_id));
      closeDrawer();
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to remove report",
      });
    }
    setBlocking(false);
  };

  const handleBulkRemove = async () => {
    setBulkWorking(true);
    let ok = 0;
    const failed = [];
    for (const id of selectedIds) {
      try {
        await deleteReport(id);
        ok += 1;
      } catch (e) {
        failed.push(id);
      }
    }
    setBulkWorking(false);
    setBulkConfirm(false);
    setSelectedIds(failed);
    if (ok) {
      toast.current?.show({
        severity: "success",
        summary: "Reports Removed",
        detail: `${ok} report${ok > 1 ? "s" : ""} removed`,
      });
    }
    if (failed.length) {
      toast.current?.show({
        severity: "error",
        summary: "Some failed",
        detail: `${failed.length} report${failed.length > 1 ? "s" : ""} could not be removed`,
      });
    }
    fetchReports();
  };

  const toggleSelect = (id) =>
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  /* ----- drawer ----- */

  const openDialog = (report, startConfirm = false) => {
    setSelectedReport(report);
    setConfirming(startConfirm);
  };

  const closeDrawer = () => {
    setSelectedReport(null);
    setConfirming(false);
  };

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeDrawer();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const copyText = (text) => {
    if (!text) return;
    navigator.clipboard?.writeText(String(text));
    toast.current?.show({ severity: "info", summary: "Copied", detail: String(text), life: 1500 });
  };

  /* ----- derived data ----- */

  const countsByUser = useMemo(() => {
    const m = {};
    reports.forEach((r) => {
      const k = r.user_id;
      if (k !== undefined && k !== null) m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [reports]);

  const summary = useMemo(() => {
    const now = Date.now();
    let today = 0;
    let week = 0;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    reports.forEach((r) => {
      const d = parseDate(r.date_time);
      if (!d) return;
      if (d >= startOfToday) today += 1;
      if (now - d.getTime() <= 7 * DAY) week += 1;
    });
    return {
      total: reports.length,
      today,
      week,
      users: Object.keys(countsByUser).length,
    };
  }, [reports, countsByUser]);

  const mostReported = useMemo(() => {
    const seen = {};
    reports.forEach((r) => {
      if (!seen[r.user_id]) seen[r.user_id] = r;
    });
    return Object.entries(countsByUser)
      .filter(([, c]) => c > 1)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([id, c]) => ({ id, count: c, row: seen[id] }));
  }, [reports, countsByUser]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const now = Date.now();
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    let list = reports.filter((r) => {
      if (focusUser && String(r.user_id) !== String(focusUser)) return false;

      if (range !== "all") {
        const d = parseDate(r.date_time);
        if (!d) return false;
        if (range === "today" && d < startOfToday) return false;
        if (range === "7d" && now - d.getTime() > 7 * DAY) return false;
        if (range === "30d" && now - d.getTime() > 30 * DAY) return false;
      }

      if (!q) return true;
      return [
        r.report_id,
        r.user_id,
        r.reported_user_name,
        r.reported_user_phone,
        r.reporter_name,
        r.report_by,
        r.reporter_phone,
        r.reason,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .some((v) => v.includes(q));
    });

    list = [...list].sort((a, b) => {
      if (sortBy === "most") {
        const diff = (countsByUser[b.user_id] || 0) - (countsByUser[a.user_id] || 0);
        if (diff !== 0) return diff;
      }
      const da = parseDate(a.date_time)?.getTime() || 0;
      const db = parseDate(b.date_time)?.getTime() || 0;
      return sortBy === "oldest" ? da - db : db - da;
    });

    return list;
  }, [reports, query, range, sortBy, focusUser, countsByUser]);

  useEffect(() => {
    setPage(1);
  }, [query, range, sortBy, focusUser, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  const pageIds = pageRows.map((r) => r.report_id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const togglePageSelect = () =>
    setSelectedIds((ids) =>
      allPageSelected
        ? ids.filter((id) => !pageIds.includes(id))
        : Array.from(new Set([...ids, ...pageIds]))
    );

  useEffect(() => {
    const valid = new Set(reports.map((r) => r.report_id));
    setSelectedIds((ids) => ids.filter((id) => valid.has(id)));
  }, [reports]);

  const otherReports = useMemo(() => {
    if (!selectedReport) return [];
    return reports.filter(
      (r) =>
        String(r.user_id) === String(selectedReport.user_id) &&
        r.report_id !== selectedReport.report_id
    );
  }, [reports, selectedReport]);

  /* ----- CSV export ----- */

  const exportCsv = () => {
    const head = [
      "ID",
      "Reported User",
      "Reported User ID",
      "Reported Contact",
      "Reported By",
      "Reporter ID",
      "Reporter Contact",
      "Reason",
      "Date & Time",
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = filtered.map((r) =>
      [
        r.report_id,
        r.reported_user_name,
        r.user_id,
        r.reported_user_phone,
        r.reporter_name || "Unknown",
        r.report_by,
        r.reporter_phone,
        r.reason,
        r.date_time,
      ]
        .map(esc)
        .join(",")
    );
    const csv = "\uFEFF" + [head.map(esc).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reported-users-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  /* ----- UI bits ----- */

  const summaryCards = [
    { label: "Total reports", value: summary.total, icon: FaFlag, tint: "bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300" },
    { label: "Today", value: summary.today, icon: FaCalendarDay, tint: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300" },
    { label: "Last 7 days", value: summary.week, icon: FaCalendarWeek, tint: "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300" },
    { label: "Users reported", value: summary.users, icon: FaUserSlash, tint: "bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300" },
  ];

  const ranges = [
    { id: "all", label: "All" },
    { id: "today", label: "Today" },
    { id: "7d", label: "7 days" },
    { id: "30d", label: "30 days" },
  ];

  const hasFilters = query || range !== "all" || focusUser;

  const PersonCell = ({ name, id, photo, phoneFallback }) => (
    <div className="flex items-center gap-3 min-w-0">
      <Avatar src={photo} name={name} size={36} />
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-900 dark:text-white truncate max-w-[160px]">
          {name || <span className="text-slate-400 font-normal">No name</span>}
        </p>
        {id ? (
          <Link
            to={`/user-profile/${id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-xs tabular-nums text-cyan-700 dark:text-cyan-400 hover:underline"
          >
            {id}
          </Link>
        ) : (
          <span className="text-xs text-slate-400">{phoneFallback || "-"}</span>
        )}
      </div>
    </div>
  );

  /* ================= render ================= */

  return (
    <div
      style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}}
      className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen"
    >
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="rp-in flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Report Request</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            User Reported Request
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Review reports sent by users and block accounts that break the rules.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={exportCsv}
            disabled={!filtered.length}
            className="rp-press inline-flex items-center gap-2 h-9 px-3.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 disabled:opacity-50"
          >
            <FaFileCsv /> Export CSV
          </button>
          <button
            type="button"
            onClick={fetchReports}
            disabled={loading}
            className="rp-press inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold disabled:opacity-60"
          >
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className={`${CARD} rp-in p-4 flex items-center gap-4`} style={{ animationDelay: `${i * 60}ms` }}>
              <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.tint}`}>
                <Icon />
              </span>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{c.label}</p>
                {loading && !reports.length ? (
                  <div className="h-6 w-10 mt-1 rounded bg-slate-200 dark:bg-slate-600 animate-pulse" />
                ) : (
                  <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">{fmt(c.value)}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Most reported */}
      {mostReported.length > 0 && (
        <div className={`${CARD} rp-in p-4 mt-4`} style={{ animationDelay: "240ms" }}>
          <div className="flex items-center gap-2 mb-3">
            <FaExclamationTriangle className="text-amber-500" />
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Reported more than once</h2>
            <span className="text-xs text-slate-500 dark:text-slate-400">Click a user to see only their reports</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {mostReported.map((m) => {
              const active = String(focusUser) === String(m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setFocusUser(active ? null : m.id)}
                  className={`rp-press inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border text-sm ${
                    active
                      ? "border-cyan-500 bg-cyan-50 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-200"
                      : "border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                  }`}
                >
                  <Avatar src={m.row?.reported_user_profile} name={m.row?.reported_user_name} size={26} />
                  <span className="max-w-[140px] truncate">{m.row?.reported_user_name || m.id}</span>
                  <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
                    {m.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Table card */}
      <div className={`${CARD} rp-in mt-4`} style={{ animationDelay: "300ms" }}>
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="relative flex-1 min-w-[220px]">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, user ID, phone or reason"
              className="w-full h-10 pl-9 pr-9 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded text-slate-400 hover:text-slate-700"
                aria-label="Clear search"
              >
                <FaTimes size={12} />
              </button>
            )}
          </div>

          <div className="inline-flex p-1 rounded-lg bg-slate-100 dark:bg-slate-700">
            {ranges.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setRange(r.id)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all active:scale-95 ${
                  range === r.id
                    ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm"
                    : "text-slate-500 dark:text-slate-300 hover:text-slate-800"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="most">Most reported first</option>
          </select>
        </div>

        {/* Active filter info */}
        {hasFilters && (
          <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 text-sm bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
            <span className="text-slate-600 dark:text-slate-300">
              Showing {fmt(filtered.length)} of {fmt(reports.length)} reports
            </span>
            {focusUser && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200 text-xs font-medium">
                User {focusUser}
                <button type="button" onClick={() => setFocusUser(null)} aria-label="Remove user filter">
                  <FaTimes size={10} />
                </button>
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setRange("all");
                setFocusUser(null);
              }}
              className="ml-auto text-cyan-700 dark:text-cyan-400 font-medium hover:underline"
            >
              Clear filters
            </button>
          </div>
        )}

        {/* Bulk action bar */}
        {selectedIds.length > 0 && (
          <div className="rp-fade flex flex-wrap items-center gap-3 px-4 py-2.5 bg-rose-50 dark:bg-rose-900/20 border-b border-rose-200 dark:border-rose-800 text-sm">
            <span className="font-medium text-rose-800 dark:text-rose-200">
              {selectedIds.length} selected
            </span>
            {!bulkConfirm ? (
              <>
                <button
                  type="button"
                  onClick={() => setBulkConfirm(true)}
                  className="rp-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-rose-600 hover:bg-rose-700 text-white font-medium"
                >
                  <FaTrash size={11} /> Remove selected
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedIds([])}
                  className="text-slate-600 dark:text-slate-300 hover:underline"
                >
                  Clear selection
                </button>
              </>
            ) : (
              <>
                <span className="text-rose-800 dark:text-rose-200">
                  Remove {selectedIds.length} report{selectedIds.length > 1 ? "s" : ""}? This cannot be undone.
                </span>
                <button
                  type="button"
                  onClick={handleBulkRemove}
                  disabled={bulkWorking}
                  className="rp-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-rose-600 hover:bg-rose-700 text-white font-medium disabled:opacity-60"
                >
                  {bulkWorking ? <FaSyncAlt className="animate-spin" size={11} /> : <FaTrash size={11} />}
                  {bulkWorking ? "Removing..." : "Yes, remove"}
                </button>
                <button
                  type="button"
                  onClick={() => setBulkConfirm(false)}
                  disabled={bulkWorking}
                  className="h-8 px-3 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-slate-700 dark:text-slate-200"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="pl-4 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={togglePageSelect}
                    aria-label="Select all on this page"
                    className="w-4 h-4 accent-cyan-600 cursor-pointer"
                  />
                </th>
                <th className="px-4 py-3 font-medium text-left">ID</th>
                <th className="px-4 py-3 font-medium text-left">Reported User</th>
                <th className="px-4 py-3 font-medium text-left whitespace-nowrap">Reported Contact</th>
                <th className="px-4 py-3 font-medium text-left">Reported By</th>
                <th className="px-4 py-3 font-medium text-left whitespace-nowrap">Reporter Contact</th>
                <th className="px-4 py-3 font-medium text-left">Reason</th>
                <th className="px-4 py-3 font-medium text-left whitespace-nowrap">Date &amp; Time</th>
                <th className="px-4 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {loading && !reports.length &&
                [0, 1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    <td colSpan={9} className="px-4 py-3">
                      <div className="h-9 rounded-md bg-slate-100 dark:bg-slate-700 animate-pulse" />
                    </td>
                  </tr>
                ))}

              {!loading && pageRows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-14 text-center">
                    <FaInbox className="mx-auto text-slate-300" size={32} />
                    <p className="mt-3 font-medium text-slate-700 dark:text-slate-200">
                      {reports.length ? "No reports match these filters" : "No pending reports"}
                    </p>
                    <p className="text-sm text-slate-500">
                      {reports.length ? "Change the search or date range." : "New reports from users will appear here."}
                    </p>
                  </td>
                </tr>
              )}

              {pageRows.map((row) => {
                const d = parseDate(row.date_time);
                const repeat = countsByUser[row.user_id] || 0;
                const isOpen = selectedReport?.report_id === row.report_id;
                return (
                  <tr
                    key={row.report_id}
                    onClick={() => openDialog(row)}
                    className={`rp-row ${isOpen ? "bg-cyan-50/70 dark:bg-cyan-900/20" : "hover:bg-slate-50 dark:hover:bg-slate-700/40"}`}
                  >
                    <td className="pl-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(row.report_id)}
                        onChange={() => toggleSelect(row.report_id)}
                        aria-label={`Select report ${row.report_id}`}
                        className="w-4 h-4 accent-cyan-600 cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3 tabular-nums text-slate-500 dark:text-slate-400">#{row.report_id}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <PersonCell
                          name={row.reported_user_name}
                          id={row.user_id}
                          photo={row.reported_user_profile}
                        />
                        {repeat > 1 && (
                          <span
                            title={`${repeat} reports on this user`}
                            className="shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300"
                          >
                            {repeat}×
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap tabular-nums text-slate-700 dark:text-slate-200">
                      {row.reported_user_phone || <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-4 py-3">
                      {row.reporter_name ? (
                        <PersonCell name={row.reporter_name} id={row.report_by} photo={row.reporter_profile} />
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                          <FaUserCircle /> Unknown
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap tabular-nums text-slate-700 dark:text-slate-200">
                      {row.reporter_phone || <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-4 py-3">
                      <p className="max-w-[220px] truncate text-slate-700 dark:text-slate-200" title={row.reason}>
                        {row.reason || <span className="text-slate-400">No reason given</span>}
                      </p>
                      {row.media && (
                        <span className="text-xs text-cyan-700 dark:text-cyan-400">Has attachment</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {d ? (
                        <>
                          <div className="text-slate-700 dark:text-slate-200">{fmtDate(d)}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">{fmtTime(d)}</div>
                        </>
                      ) : (
                        row.date_time || "-"
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDialog(row);
                          }}
                          className="rp-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                        >
                          <FaEye size={12} /> View
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDialog(row, "block");
                          }}
                          className="rp-press inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-rose-50 text-rose-700 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300 text-sm font-medium"
                        >
                          <FaBan size={12} /> Block
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDialog(row, "remove");
                          }}
                          title="Remove report"
                          aria-label="Remove report"
                          className="rp-press w-8 h-8 inline-flex items-center justify-center rounded-md border border-slate-200 dark:border-slate-600 text-slate-500 hover:text-rose-600 hover:border-rose-200 hover:bg-rose-50 dark:hover:bg-rose-900/30"
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filtered.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 dark:border-slate-700 text-sm">
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
              Rows per page
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="h-8 px-2 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800"
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-slate-600 dark:text-slate-300 tabular-nums">
                {fmt((safePage - 1) * pageSize + 1)}–{fmt(Math.min(safePage * pageSize, filtered.length))} of{" "}
                {fmt(filtered.length)}
              </span>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="rp-press w-8 h-8 rounded-md border border-slate-200 dark:border-slate-600 flex items-center justify-center disabled:opacity-40"
                  aria-label="Previous page"
                >
                  <FaChevronLeft size={11} />
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="rp-press w-8 h-8 rounded-md border border-slate-200 dark:border-slate-600 flex items-center justify-center disabled:opacity-40"
                  aria-label="Next page"
                >
                  <FaChevronRight size={11} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ================= Detail drawer ================= */}
      {selectedReport && (
        <div className="fixed inset-0 flex justify-end" style={{ zIndex: 1100 }}>
          <div className="rp-fade absolute inset-0 bg-slate-900/40" onClick={closeDrawer} />
          <aside className="rp-slide relative w-full max-w-md h-full bg-white dark:bg-secondary-dark-bg shadow-2xl flex flex-col">
            {/* Drawer header */}
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Report Details</p>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Report #{selectedReport.report_id}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {(() => {
                    const d = parseDate(selectedReport.date_time);
                    return d ? `${fmtDate(d)}, ${fmtTime(d)}` : selectedReport.date_time;
                  })()}
                </p>
              </div>
              <button
                type="button"
                onClick={closeDrawer}
                className="rp-press w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>

            {/* Drawer body */}
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
              {/* Reported user */}
              <section>
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2">Reported User</h3>
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                  <div className="flex items-center gap-3">
                    <Avatar
                      src={selectedReport.reported_user_profile}
                      name={selectedReport.reported_user_name}
                      size={52}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-900 dark:text-white truncate">
                        {selectedReport.reported_user_name || "No name"}
                      </p>
                      <p className="text-sm text-slate-500 tabular-nums">ID {selectedReport.user_id}</p>
                    </div>
                    {(countsByUser[selectedReport.user_id] || 0) > 1 && (
                      <span className="text-xs font-semibold px-2 py-1 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
                        {countsByUser[selectedReport.user_id]} reports
                      </span>
                    )}
                  </div>
                  {selectedReport.reported_user_phone && (
                    <div className="mt-3 flex items-center gap-2 text-sm">
                      <FaPhoneAlt className="text-slate-400" size={12} />
                      <a href={`tel:${selectedReport.reported_user_phone}`} className="text-slate-700 dark:text-slate-200 hover:underline tabular-nums">
                        {selectedReport.reported_user_phone}
                      </a>
                      <button
                        type="button"
                        onClick={() => copyText(selectedReport.reported_user_phone)}
                        className="p-1 text-slate-400 hover:text-slate-700"
                        aria-label="Copy phone"
                      >
                        <FaCopy size={12} />
                      </button>
                    </div>
                  )}
                  {selectedReport.user_id && (
                    <Link
                      to={`/user-profile/${selectedReport.user_id}`}
                      onClick={closeDrawer}
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-cyan-700 dark:text-cyan-400 hover:underline"
                    >
                      Open full profile <FaExternalLinkAlt size={10} />
                    </Link>
                  )}
                </div>
              </section>

              {/* Reason */}
              <section>
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2">Reason</h3>
                <p className="rounded-xl bg-slate-50 dark:bg-slate-800 p-4 text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap break-words">
                  {selectedReport.reason || "No reason given"}
                </p>
              </section>

              {/* Media */}
              {selectedReport.media && (
                <section>
                  <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2">Media</h3>
                  {String(selectedReport.media_type || "").includes("image") ? (
                    <a href={selectedReport.media} target="_blank" rel="noreferrer">
                      <img
                        src={selectedReport.media}
                        alt="report media"
                        className="w-full rounded-xl border border-slate-200 dark:border-slate-700"
                      />
                    </a>
                  ) : String(selectedReport.media_type || "").includes("video") ? (
                    <video src={selectedReport.media} controls className="w-full rounded-xl" />
                  ) : (
                    <a
                      href={`/uploads/${selectedReport.media}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-medium text-cyan-700 dark:text-cyan-400 hover:underline"
                    >
                      View File <FaExternalLinkAlt size={10} />
                    </a>
                  )}
                </section>
              )}

              {/* Reporter */}
              <section>
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2">Reported By</h3>
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                  {selectedReport.reporter_name ? (
                    <div className="flex items-center gap-3">
                      <Avatar src={selectedReport.reporter_profile} name={selectedReport.reporter_name} size={40} />
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 dark:text-white truncate">{selectedReport.reporter_name}</p>
                        {selectedReport.report_by && (
                          <Link
                            to={`/user-profile/${selectedReport.report_by}`}
                            onClick={closeDrawer}
                            className="text-sm tabular-nums text-cyan-700 dark:text-cyan-400 hover:underline"
                          >
                            ID {selectedReport.report_by}
                          </Link>
                        )}
                      </div>
                    </div>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                      <FaUserCircle /> Unknown
                    </span>
                  )}
                  {selectedReport.reporter_phone && (
                    <div className="mt-3 flex items-center gap-2 text-sm">
                      <FaPhoneAlt className="text-slate-400" size={12} />
                      <a href={`tel:${selectedReport.reporter_phone}`} className="text-slate-700 dark:text-slate-200 hover:underline tabular-nums">
                        {selectedReport.reporter_phone}
                      </a>
                      <button
                        type="button"
                        onClick={() => copyText(selectedReport.reporter_phone)}
                        className="p-1 text-slate-400 hover:text-slate-700"
                        aria-label="Copy phone"
                      >
                        <FaCopy size={12} />
                      </button>
                    </div>
                  )}
                </div>
              </section>

              {/* Other reports on same user */}
              {otherReports.length > 0 && (
                <section>
                  <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2">
                    Other reports on this user ({otherReports.length})
                  </h3>
                  <ul className="space-y-2">
                    {otherReports.slice(0, 5).map((r) => {
                      const d = parseDate(r.date_time);
                      return (
                        <li key={r.report_id}>
                          <button
                            type="button"
                            onClick={() => openDialog(r)}
                            className="rp-press w-full text-left rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/40"
                          >
                            <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
                              <span>#{r.report_id}</span>
                              <span>{d ? `${fmtDate(d)}, ${fmtTime(d)}` : r.date_time}</span>
                            </div>
                            <p className="text-sm text-slate-700 dark:text-slate-200 truncate">{r.reason || "No reason given"}</p>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}
            </div>

            {/* Drawer footer */}
            <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              {!confirming ? (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={closeDrawer}
                    className="rp-press h-10 px-4 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirming("remove")}
                    className="rp-press flex-1 h-10 rounded-lg border border-rose-200 dark:border-rose-800 bg-white dark:bg-secondary-dark-bg text-rose-700 dark:text-rose-300 hover:bg-rose-50 text-sm font-semibold inline-flex items-center justify-center gap-2"
                  >
                    <FaTrash size={12} /> Remove
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirming("block")}
                    className="rp-press flex-1 h-10 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2"
                  >
                    <FaBan /> Block User
                  </button>
                </div>
              ) : confirming === "remove" ? (
                <div className="rp-fade">
                  <p className="text-sm text-slate-800 dark:text-slate-100">
                    Remove report <b>#{selectedReport.report_id}</b>?
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                    The report will be deleted. The user will not be blocked.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirming(false)}
                      disabled={blocking}
                      className="rp-press flex-1 h-10 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemove(selectedReport.report_id)}
                      disabled={blocking}
                      className="rp-press flex-1 h-10 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      {blocking ? <FaSyncAlt className="animate-spin" size={12} /> : <FaTrash size={12} />}
                      {blocking ? "Removing..." : "Yes, remove"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rp-fade">
                  <p className="text-sm text-slate-800 dark:text-slate-100">
                    Block <b>{selectedReport.reported_user_name || `user ${selectedReport.user_id}`}</b>?
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                    This report will be closed and the user will be blocked.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirming(false)}
                      disabled={blocking}
                      className="rp-press flex-1 h-10 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => acceptRequest(selectedReport.report_id)}
                      disabled={blocking}
                      className="rp-press flex-1 h-10 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      {blocking ? <FaSyncAlt className="animate-spin" size={12} /> : <FaBan />}
                      {blocking ? "Blocking..." : "Yes, block"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default ReportedUser;